import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook — updates <title>, meta[name=description]
 * and <link rel=canonical> on route change. Reverts to sensible defaults
 * when the component unmounts.
 */
export default function useSEO({ title, description, canonical }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content) => {
      if (!content) return null;
      let el = document.querySelector(`meta[name="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const descSnap = setMeta("description", description);
    const canonicalSnap = setLink("canonical", canonical);

    return () => {
      document.title = prevTitle;
      if (descSnap) {
        if (descSnap.created) descSnap.el.remove();
        else if (descSnap.prev !== null) descSnap.el.setAttribute("content", descSnap.prev);
      }
      if (canonicalSnap) {
        if (canonicalSnap.created) canonicalSnap.el.remove();
        else if (canonicalSnap.prev !== null) canonicalSnap.el.setAttribute("href", canonicalSnap.prev);
      }
    };
  }, [title, description, canonical]);
}
